import { Controller } from '@hotwired/stimulus';
import VanillaCalendar from 'vanilla-calendar-pro';

function toISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function parseISODate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function formatLabel(date, lang) {
    return date.toLocaleDateString(lang, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export default class extends Controller {
    connect() {
        const data = this.element.dataset;
        this.bookingUrl = data.bookingUrl || '';
        this.lang = (document.documentElement.lang || 'en').split('-')[0];

        this.checkinField = this.element.querySelector('.accommodations-filter__field_checkin');
        this.checkoutField = this.element.querySelector('.accommodations-filter__field_checkout');
        this.checkinValue = this.element.querySelector('[data-value="checkin"]');
        this.checkoutValue = this.element.querySelector('[data-value="checkout"]');

        this.guestsField = this.element.querySelector('.accommodations-filter__field_guests');
        this.guestsValue = this.element.querySelector('[data-value="guests"]');
        this.guestsDropdown = this.element.querySelector('.accommodations-filter__guests-dropdown');
        this.adultsCount = this.element.querySelector('[data-count="adults"]');

        const params = new URLSearchParams(window.location.search);
        const paramCheckin = params.get('checkin');
        const paramCheckout = params.get('checkout');
        const paramAdults = parseInt(params.get('adults'), 10);

        this.adults = paramAdults > 0 ? paramAdults : 2;
        this.checkin = paramCheckin ? parseISODate(paramCheckin) : (data.checkin ? parseISODate(data.checkin) : new Date());
        this.checkout = paramCheckout ? parseISODate(paramCheckout) : (data.checkout ? parseISODate(data.checkout) : new Date());

        this.updateDateLabels();
        this.updateGuestsLabel();

        this.calendarEl = this.element.querySelector('.accommodations-filter__calendar');

        this.calendar = new VanillaCalendar(this.calendarEl, {
            date: {
                min: toISODate(new Date()),
            },
            settings: {
                lang: this.lang,
                range: {
                    disablePast: true,
                },
                selection: {
                    day: 'multiple-ranged',
                },
                selected: {
                    dates: [toISODate(this.checkin), toISODate(this.checkout)],
                },
                visibility: {
                    theme: 'light',
                },
            },
            actions: {
                clickDay: (event, self) => {
                    if (self.selectedDates.length >= 2) {
                        this.checkin = parseISODate(self.selectedDates[0]);
                        this.checkout = parseISODate(self.selectedDates[self.selectedDates.length - 1]);
                        this.updateDateLabels();
                        this.closeCalendar();
                    }
                },
            },
        });
        this.calendar.init();

        this.outsideClickListener = (e) => {
            // VanillaCalendar re-renders (replaces) the clicked day button on selection, which detaches
            // it from the DOM while this event is still bubbling. By the time it reaches `document`,
            // `this.calendarEl.contains(e.target)` would wrongly report false for that detached node,
            // closing the calendar after picking only the checkin date. `composedPath()` is captured
            // at dispatch time, before any of that re-rendering happens, so it stays accurate.
            const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];

            if (!path.includes(this.calendarEl) && !path.includes(this.checkinField) && !path.includes(this.checkoutField)) {
                this.closeCalendar();
            }
            if (!path.includes(this.guestsField)) {
                this.closeGuests();
            }
        };
        document.addEventListener('click', this.outsideClickListener);
    }

    disconnect() {
        document.removeEventListener('click', this.outsideClickListener);
    }

    updateDateLabels() {
        this.checkinValue.innerText = formatLabel(this.checkin, this.lang);
        this.checkoutValue.innerText = formatLabel(this.checkout, this.lang);
    }

    toggleCalendar(e) {
        e.stopPropagation();
        this.closeGuests();
        this.calendarEl.classList.toggle('accommodations-filter__calendar_visible');
    }

    closeCalendar() {
        this.calendarEl.classList.remove('accommodations-filter__calendar_visible');
    }

    toggleGuests(e) {
        e.stopPropagation();
        this.closeCalendar();
        this.guestsDropdown.classList.toggle('accommodations-filter__guests-dropdown_visible');
    }

    closeGuests() {
        this.guestsDropdown.classList.remove('accommodations-filter__guests-dropdown_visible');
    }

    increaseAdults(e) {
        e.stopPropagation();
        if (this.adults < 10) {
            this.adults++;
            this.updateGuestsLabel();
        }
    }

    decreaseAdults(e) {
        e.stopPropagation();
        if (this.adults > 1) {
            this.adults--;
            this.updateGuestsLabel();
        }
    }

    updateGuestsLabel() {
        this.adultsCount.innerText = this.adults;
        const label = this.adults === 1 ? this.guestsField.dataset.labelGuestSingular : this.guestsField.dataset.labelGuestPlural;
        this.guestsValue.innerText = `${this.adults} ${label}`;
    }

    search(e) {
        e.preventDefault();

        if (!this.bookingUrl) {
            return;
        }

        const url = new URL(this.bookingUrl, window.location.origin);
        url.searchParams.set('checkin', toISODate(this.checkin));
        url.searchParams.set('checkout', toISODate(this.checkout));
        url.searchParams.set('adults', this.adults);

        window.location.href = url.toString();
    }
}
